/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { CarouselProps } from '@/components/blocks/ProductDetails/Carousel';
import { productDetailsThumbnailSliderSX } from '@/components/blocks/ProductDetails/styles/thumbnailSlider';
import { productDetailsThumbnailRibbonsSX } from '@/components/blocks/ProductDetails/styles/thumbnailRibbons';
import { ProductImage } from '@/components/blocks/ProductImage';
import { useTheme } from '@mui/material/styles';
import {
	Box,
	ButtonBase,
	ClickAwayListener,
	Stack,
	Tooltip,
	tooltipClasses,
	TooltipProps,
	useMediaQuery,
} from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Pagination, Zoom } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import { keepOriginalSizeOnZoomOut } from '@/utils/swiperZoom';
import React, { FC, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ProductDetailsAddToWishList } from '@/components/blocks/ProductDetails/AddToWishList';
import styled from '@emotion/styled';
import { Img } from '@/components/blocks/MaterialImage';
import ShareIcon from '@mui/icons-material/Share';
import { CounterSlides } from '@/components/content/ProductDetails/parts/CounterSlides';
import { OpenFullImage } from '@/components/content/ProductDetails/parts/OpenFullImage';
import { ProductRibbons } from '@/components/blocks/Ribbons';
import { mergeRibbonsForProduct } from '@/utils/mergeRibbonsForProduct';

const MD_UP_DIMS = { slideWidth: 12, slideHeight: 15, visibleSlides: 8 };
const SM_DIMS = { slideWidth: 5, slideHeight: 3, visibleSlides: 1 };
const XS_DIMS = { slideWidth: 1, slideHeight: 1, visibleSlides: 1 };

export const ProductDetailsThumbnailCarousel: FC<CarouselProps> = ({
	slides,
	choose,
	index,
	ribbons,
	onOpenFullImage,
	onClick,
	carouselRef,
}) => {
	const theme = useTheme();
	const isSm = useMediaQuery(theme.breakpoints.down('md'));
	const isXs = useMediaQuery(theme.breakpoints.down('sm'));
	const isMd = useMediaQuery(theme.breakpoints.down('lg'));
	const isThumbnail = !isSm;
	const { visibleSlides } = isXs ? XS_DIMS : isSm ? SM_DIMS : MD_UP_DIMS;
	const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
		<Tooltip {...props} classes={{ popper: className }} />
	))(() => ({
		[`& .${tooltipClasses.tooltip}`]: {
			backgroundColor: '#F1F1F1',
			color: 'rgba(0, 0, 0, 0.87)',
			fontSize: 'var(--font-size-tiny)',
		},
		[`& .${tooltipClasses.arrow}`]: {
			color: '#F1F1F1',
		},
	}));

	const [activeIndex, setActiveIndex] = useState(0);
	const [openShare, setOpenShare] = useState(false);
	const swiperRef = useRef<SwiperType | null>(null);

	useImperativeHandle(
		carouselRef,
		() => ({
			goToSlide: (i: number) => {
				if (isSm) {
					swiperRef.current?.slideToLoop(i, 0);
				}
			},
		}),
		[isSm]
	);

	const [urlCompleta, setUrlCompleta] = useState('');

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const urlActual = window.location.href;
			setUrlCompleta(urlActual);
		}
	}, []);

	const handleShare = () => {
		setOpenShare((prev) => !prev);
	};

	const handleTooltipClose = () => {
		setOpenShare(false);
	};

	const handleShareWhatsapp = () => {
		const message = encodeURIComponent(
			`¡Te recomiendo este producto que vi en dpstreet! ${urlCompleta}`
		);
		const linkWhatsApp = `https://wa.me/?text=${message}`;
		window.open(linkWhatsApp, '_blank');
	};

	const handleShareFacebook = () => {
		window?.open(`https://www.facebook.com/sharer.php?s=100&u=${window?.location?.href}`, '_blank');
	};
	const imagesToRender = slides.map(({ thumbnail, name, fullImage }) => {
		const src = isXs ? fullImage : isSm ? fullImage : thumbnail;
		return { src, name };
	});

	return (
		<Box sx={{ position: 'relative', zIndex: 1000 }}>
			{isSm ? (
				<>
					<Box
						// eslint-disable-next-line react/forbid-component-props
						className="product-image-container"
						sx={productDetailsThumbnailRibbonsSX}
					>
						<ProductRibbons ribbons={mergeRibbonsForProduct(ribbons)} />
					</Box>
					<ProductDetailsAddToWishList isIconFavorite inThumbnail />
					<CounterSlides selectedNumber={activeIndex + 1} totalSlides={imagesToRender.length} />
					<OpenFullImage
						onClick={() => {
							choose(activeIndex);
							onOpenFullImage?.();
						}}
					/>
				</>
			) : null}
			{isSm ? (
				<ClickAwayListener onClickAway={handleTooltipClose}>
					<Box>
						<LightTooltip
							open={openShare}
							sx={{
								'& .MuiTooltip-arrow': {
									backgroundColor: '#F1F1F1',
								},
							}}
							title={
								<Stack direction="row" spacing={2} padding={0.5}>
									<Img
										id="pdp-facebook-share"
										src="/images/pdp-facebook.png"
										alt="Facebook"
										sx={{ width: '27px', height: '27px', cursor: 'pointer' }}
										onClick={handleShareFacebook}
									/>
									<Img
										id="pdp-instagram-share"
										src="/images/whatsapp.png"
										alt="Instagram"
										sx={{ width: '27px', height: '27px', cursor: 'pointer' }}
										onClick={handleShareWhatsapp}
									/>
								</Stack>
							}
							arrow
						>
							<ButtonBase
								id="pdp-share-button"
								aria-label="share"
								sx={{
									backgroundColor: '#F1F1F1',
									position: 'absolute',
									bottom: '65px',
									right: '19px',
									zIndex: 1001,
									width: '40px',
									height: '40px',
									borderRadius: '50%',
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
								}}
								onClick={handleShare}
							>
								<ShareIcon sx={{ color: '#505050', fontSize: '20px' }} />
							</ButtonBase>
						</LightTooltip>
					</Box>
				</ClickAwayListener>
			) : null}

			<Box sx={productDetailsThumbnailSliderSX(imagesToRender.length)}>
				<Swiper
					key={isSm ? 'mobile' : 'desktop'}
					modules={isSm ? [Pagination, Zoom] : [Pagination]}
					zoom={isSm ? { maxRatio: 3, toggle: false } : false}
					onInit={keepOriginalSizeOnZoomOut}
					spaceBetween={isSm ? 0 : 3}
					slidesPerView={isSm ? visibleSlides : 'auto'}
					slidesPerGroup={isSm ? visibleSlides : 1}
					direction={isSm ? 'horizontal' : 'vertical'}
					loop={true}
					allowTouchMove={isMd}
					onSwiper={(swiper: SwiperType) => {
						swiperRef.current = swiper;
					}}
					onSlideChange={(swiper: any) => setActiveIndex(swiper?.realIndex)}
					pagination={
						isSm
							? {
									clickable: true,
							  }
							: false
					}
					style={{
						height: 'auto', // naturalSlideHeight from original
						width: '100%',
					}}
				>
					{imagesToRender.map(({ name = '', src }, i) => (
						<SwiperSlide key={`${isSm}-${i}`} style={!isSm ? { height: 'auto' } : undefined}>
							<Box
								onClick={(event) => {
									choose(i);
									if (!src.includes('.mp4')) {
										onClick?.(event);
									}
								}}
								onFocus={() => choose(i)}
								sx={{
									width: '100%',
									height: !isSm ? 'auto' : '100%',
									aspectRatio: !isSm ? '1/1' : undefined,
									paddingBottom: {
										xs: 'none',
									},
									cursor: 'pointer',
								}}
							>
								<ProductImage
									src={src}
									alt={name}
									ribbons={[]}
									isThumbnail={isThumbnail}
									isSelected={!isSm ? i === index : false}
									isPDP={isSm}
									showMobileOverlays={false}
									zoomable={isSm}
									indexImage={i + 1}
									totalSlides={imagesToRender.length}
									onOpenFullImage={onOpenFullImage}
									sx={{
										width: '100%',
										height: '100%',
										display: 'block',
										border: '1px solid',
										borderColor: !isSm && i === index ? theme.palette.primary.main : 'transparent',
										boxSizing: 'border-box',
										'& img': {
											width: '100%',
											height: '100%',
											objectFit: 'cover',
										},
									}}
								/>
							</Box>
						</SwiperSlide>
					))}
				</Swiper>
			</Box>
		</Box>
	);
};
